import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableRow, Stack, useTheme, useMediaQuery } from '@mui/material';
import authService from '../../api-authorization/AuthorizeService';
import toast from 'react-hot-toast';
import styles from './AdminDeviceDiagnostic.module.css';

const DIAGNOSTIC_ENDPOINT = '/api/admin/device-diagnostic';
const START_DIAGNOSTIC_ENDPOINT = '/api/admin/start-device-diagnostic';

function formatLocalTime(utcString) {
  if (!utcString) return '';
  const date = new Date(utcString);
  return date.toLocaleString();
}

function formatNumber(val, decCount = 2) {
  return typeof val === 'number' && !isNaN(val) ? val.toFixed(decCount) : (val ?? '-');
}

function boomDataToCsv(boomData) {
  if (!Array.isArray(boomData) || boomData.length === 0) return '';
  const headers = ['Name', 'Status', 'Boom Width (ft)', 'Total Distance (ft)', 'Total Distance (mi)', 'Total Area (ac)'];
  const rows = boomData.map(b => [
    `"${b.name}"`,
    b.isActive ? 'Active' : 'Inactive',
    formatNumber(b.boomWidthFt),
    formatNumber(b.totalDistanceFt),
    formatNumber(b.totalDistanceMi),
    formatNumber(b.totalAcres, 5)
  ]);
  return [headers.join(','), ...rows.map(r => r.join(','))].join('\r\n');
}

function saveCsvFile(csv, filename) {
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

const AdminDeviceDiagnostic = () => {
  const [searchParams] = useSearchParams();
  const tractorId = searchParams.get('tractorId');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tractor, setTractor] = useState(null);
  const [devices, setDevices] = useState([]);
  const [diagnostic, setDiagnostic] = useState(null);
  const [loading, setLoading] = useState(false);
  const [heartbeatLoading, setHeartbeatLoading] = useState(false);
  const intervalRef = useRef();
  const hasAutoReset = useRef(false);

  function getLastResetUtc() {
    return localStorage.getItem('adminDeviceDiagnosticResetTime') || '';
  }

  // Fetch tractor details
  useEffect(() => {
    const fetchTractor = async () => {
      if (!tractorId) return;

      try {
        const token = await authService.getAccessToken();
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/admin/tractors/all`,
          {
            headers: !token ? {} : { Authorization: `Bearer ${token}` }
          }
        );

        if (response.ok) {
          const tractors = await response.json();
          const foundTractor = tractors.find(t => t.id === parseInt(tractorId));
          setTractor(foundTractor);
        }
      } catch (error) {
        console.error("Error fetching tractor:", error);
      }
    };

    fetchTractor();
  }, [tractorId]);

  // Fetch devices for the tractor
  useEffect(() => {
    const fetchDevices = async () => {
      if (!tractorId) return;
      try {
        const token = await authService.getAccessToken();
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/admin/devices/all-by-tractor?tractorId=${tractorId}`,
          {
            headers: !token ? {} : { Authorization: `Bearer ${token}` }
          }
        );
        if (response.ok) {
          const devicesData = await response.json();
          setDevices(devicesData);
        } else {
          setDevices([]);
        }
      } catch (error) {
        setDevices([]);
        console.error("Error fetching devices:", error);
      }
    };

    fetchDevices();
  }, [tractorId]);

  // Get the first device serial number for diagnostic (or handle multiple if needed)
  const deviceSerialNumber = devices.length > 0 ? devices[0].serialNumber : null;

  useEffect(() => {
    if (!deviceSerialNumber) return;

    async function fetchDiagnostic() {
      setLoading(true);
      try {
        const utcTime = getLastResetUtc();
        const res = await fetch(
          `${DIAGNOSTIC_ENDPOINT}?startTimeUtc=${encodeURIComponent(utcTime)}&deviceSerialNumber=${encodeURIComponent(deviceSerialNumber)}`
        );
        const data = await res.json();
        setDiagnostic(data);
      } catch (err) {
        setDiagnostic(null);
      } finally {
        setLoading(false);
      }
    }
    fetchDiagnostic();
    intervalRef.current = setInterval(fetchDiagnostic, 1000);
    return () => clearInterval(intervalRef.current);
  }, [deviceSerialNumber]);

  // Reset button handler
  const handleReset = async () => {
    if (!deviceSerialNumber) return;
    try {
      setLoading(true);
      // Get server UTC time from the endpoint
      const res = await fetch(START_DIAGNOSTIC_ENDPOINT, { method: 'POST' });
      const serverUtc = await res.json();
      localStorage.setItem('adminDeviceDiagnosticResetTime', serverUtc);

      // Now fetch diagnostic data using the server time
      const diagRes = await fetch(
        `${DIAGNOSTIC_ENDPOINT}?startTimeUtc=${encodeURIComponent(serverUtc)}&deviceSerialNumber=${encodeURIComponent(deviceSerialNumber)}`
      );
      const data = await diagRes.json();
      setDiagnostic(data);
    } catch (err) {
      setDiagnostic(null);
    } finally {
      setLoading(false);
    }
  };

  // Auto-reset once when device is available
  useEffect(() => {
    if (!deviceSerialNumber || hasAutoReset.current) return;
    hasAutoReset.current = true;
    handleReset();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceSerialNumber]);

  const handleForceHeartbeats = async () => {
    if (!tractorId) return;
    setHeartbeatLoading(true);
    try {
      const token = await authService.getAccessToken();
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/diagnostics/force-heartbeats`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: JSON.stringify({ tractorId: parseInt(tractorId), timeOutMin: 120 })
        }
      );
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      if (!data.code) throw new Error('No code returned');
      const poll = setInterval(async () => {
        try {
          const statusRes = await fetch(
            `${process.env.REACT_APP_BASE_URL}/api/diagnostics/force-heartbeats?code=${data.code}`,
            { headers: token ? { Authorization: `Bearer ${token}` } : {} }
          );
          if (statusRes.ok) {
            const statusData = await statusRes.json();
            if (statusData.completed) {
              clearInterval(poll);
              setHeartbeatLoading(false);
              toast.success('Force heartbeat collection started for 2 hours.');
            }
          }
        } catch { clearInterval(poll); setHeartbeatLoading(false); }
      }, 5000);
    } catch {
      setHeartbeatLoading(false);
      toast.error('Failed to force heartbeat collection.');
    }
  };

  const handleExportBoomData = () => {
    if (diagnostic && Array.isArray(diagnostic.boomData) && diagnostic.boomData.length > 0) {
      const csv = boomDataToCsv(diagnostic.boomData);
      saveCsvFile(csv, 'boom-results.csv');
    }
  };

  return (
    <div className={styles.container}>
      <Box sx={{ p: { xs: 0.5, sm: 2 }, width: '100%', boxSizing: 'border-box' }}>
        <Typography variant={isMobile ? 'h5' : 'h3'} sx={{ pb: 0.5, fontWeight: 700 }}>
          {tractor ? `${tractor.name}` : ''}
        </Typography>
        <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ pb: 1.5, color: 'text.secondary' }}>
          Device Diagnostics
        </Typography>
        {isMobile ? (
          /* Mobile: stack vertically */
          <Stack direction="column" spacing={1.5} sx={{ mb: 3 }}>
            <Stack direction="row" spacing={1.5} alignItems="stretch">
              <Button variant="contained" color="primary" onClick={handleReset} size="medium" fullWidth sx={{ height: 36 }}>
                Reset
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleForceHeartbeats}
                disabled={heartbeatLoading}
                size="medium"
                fullWidth
                sx={{ height: 36, whiteSpace: 'nowrap', fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
              >
                {heartbeatLoading
                  ? <><CircularProgress size={14} sx={{ color: '#fff', mr: 0.75 }} />Sending...</>
                  : 'Force Heartbeat (2h)'}
              </Button>
            </Stack>
            <Typography variant="body2" sx={{ visibility: loading ? 'visible' : 'hidden' }}>
              Loading...
            </Typography>
            {diagnostic && (
              <Box sx={{ bgcolor: diagnostic.isActive ? '#43a047' : '#e53935', color: '#fff', px: 2, py: 1, borderRadius: 2, textAlign: 'center', fontWeight: 700, fontSize: '1rem' }}>
                Heartbeats within 30 sec
              </Box>
            )}
          </Stack>
        ) : (
          /* Desktop: 3-column grid � Reset left | Heartbeat center | Status right */
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 2, mb: 3 }}>
            {/* Left: Reset + loading */}
            <Stack direction="row" spacing={2} alignItems="center">
              <Button variant="contained" color="primary" onClick={handleReset} size="large">
                Reset
              </Button>
              <Typography variant="body1" sx={{ visibility: loading ? 'visible' : 'hidden', whiteSpace: 'nowrap' }}>
                Loading...
              </Typography>
            </Stack>
            {/* Center: Force Heartbeat */}
            <Button
              variant="contained"
              color="primary"
              onClick={handleForceHeartbeats}
              disabled={heartbeatLoading}
              size="large"
              sx={{ whiteSpace: 'nowrap' }}
            >
              {heartbeatLoading
                ? <><CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />Sending...</>
                : 'Force Heartbeat (2h)'}
            </Button>
            {/* Right: Status badge */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              {diagnostic && (
                <Box sx={{ bgcolor: diagnostic.isActive ? '#43a047' : '#e53935', color: '#fff', px: 3, py: 1, borderRadius: 2, fontWeight: 700, fontSize: '1rem', flexShrink: 0 }}>
                  Heartbeats within 30 sec
                </Box>
              )}
            </Box>
          </Box>
        )}
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Typography variant="h6" gutterBottom fontWeight={600}>
            General
          </Typography>
          {diagnostic ? (
            <TableContainer>
              <Table size="medium">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '1rem', width: '40%' }}>Sprayer Name</TableCell>
                    <TableCell sx={{ fontSize: '1rem' }}>{diagnostic.tractorName}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>Serial Number</TableCell>
                    <TableCell sx={{ fontSize: '1rem' }}>{deviceSerialNumber}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>Nozzle Profile Name</TableCell>
                    <TableCell sx={{ fontSize: '1rem' }}>{formatNumber(diagnostic.nozzleProfileName)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>Start Time</TableCell>
                    <TableCell sx={{ fontSize: '1rem' }}>{formatLocalTime(getLastResetUtc())}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>Elapsed</TableCell>
                    <TableCell sx={{ fontSize: '1rem' }}>{diagnostic.elapsed}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography>No data available.</Typography>
          )}
        </Paper>
        {diagnostic && Array.isArray(diagnostic.boomData) && diagnostic.boomData.length > 0 ? (
          <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="h6" fontWeight={600}>Boom Results</Typography>
              <Button variant="outlined" size="medium" onClick={handleExportBoomData}>Save CSV</Button>
            </Box>
            {isMobile ? (
              <Stack spacing={2}>
                {diagnostic.boomData.map((boom, idx) => (
                  <Box key={idx} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 1.5 }}>
                    <Table size="small">
                      <TableBody>
                        <TableRow><TableCell sx={{ fontWeight: 700 }}>Name</TableCell><TableCell>{boom.name}</TableCell></TableRow>
                        <TableRow><TableCell sx={{ fontWeight: 700 }}>Status</TableCell><TableCell><Box sx={{ bgcolor: boom.isActive ? '#43a047' : '#e53935', color: '#fff', px: 1.5, py: 0.5, borderRadius: 1, display: 'inline-block', fontSize: '0.85rem', fontWeight: 600 }}>{boom.isActive ? 'Active' : 'Inactive'}</Box></TableCell></TableRow>
                        <TableRow><TableCell sx={{ fontWeight: 700 }}>Boom Width (ft)</TableCell><TableCell>{formatNumber(boom.boomWidthFt)}</TableCell></TableRow>
                        <TableRow><TableCell sx={{ fontWeight: 700 }}>Total Distance (ft)</TableCell><TableCell>{formatNumber(boom.totalDistanceFt)}</TableCell></TableRow>
                        <TableRow><TableCell sx={{ fontWeight: 700 }}>Total Distance (mi)</TableCell><TableCell>{formatNumber(boom.totalDistanceMi)}</TableCell></TableRow>
                        <TableRow><TableCell sx={{ fontWeight: 700 }}>Total Area (ac)</TableCell><TableCell>{formatNumber(boom.totalAcres, 5)}</TableCell></TableRow>
                      </TableBody>
                    </Table>
                  </Box>
                ))}
              </Stack>
            ) : (
              <TableContainer>
                <Table size="medium">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>Boom Width (ft)</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>Total Distance (ft)</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>Total Distance (mi)</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>Total Area (ac)</TableCell>
                    </TableRow>
                    {diagnostic.boomData.map((boom, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ fontSize: '1rem' }}>{boom.name}</TableCell>
                        <TableCell>
                          <Box sx={{ bgcolor: boom.isActive ? '#43a047' : '#e53935', color: '#fff', px: 2, py: 0.5, borderRadius: 1, display: 'inline-block', fontSize: '0.9rem', fontWeight: 600, minWidth: 90, textAlign: 'center' }}>
                            {boom.isActive ? 'Active' : 'Inactive'}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: '1rem' }}>{formatNumber(boom.boomWidthFt)}</TableCell>
                        <TableCell sx={{ fontSize: '1rem' }}>{formatNumber(boom.totalDistanceFt)}</TableCell>
                        <TableCell sx={{ fontSize: '1rem' }}>{formatNumber(boom.totalDistanceMi)}</TableCell>
                        <TableCell sx={{ fontSize: '1rem' }}>{formatNumber(boom.totalAcres, 5)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        ) : (
          <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="h6" fontWeight={600}>Boom Results</Typography>
              <Button variant="outlined" size="medium" onClick={handleExportBoomData}>Save CSV</Button>
            </Box>
            {isMobile ? (
              <Stack spacing={2}>
                {[1, 2, 3].map((idx) => (
                  <Box key={idx} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 1.5 }}>
                    <Table size="small">
                      <TableBody>
                        <TableRow><TableCell sx={{ fontWeight: 700 }}>Name</TableCell><TableCell>Boom {idx}</TableCell></TableRow>
                        <TableRow><TableCell sx={{ fontWeight: 700 }}>Status</TableCell><TableCell><Box sx={{ bgcolor: '#e53935', color: '#fff', px: 1.5, py: 0.5, borderRadius: 1, display: 'inline-block', fontSize: '0.85rem', fontWeight: 600 }}>Unknown</Box></TableCell></TableRow>
                        <TableRow><TableCell sx={{ fontWeight: 700 }}>Boom Width (ft)</TableCell><TableCell>{formatNumber(0)}</TableCell></TableRow>
                        <TableRow><TableCell sx={{ fontWeight: 700 }}>Total Distance (ft)</TableCell><TableCell>{formatNumber(0)}</TableCell></TableRow>
                        <TableRow><TableCell sx={{ fontWeight: 700 }}>Total Distance (mi)</TableCell><TableCell>{formatNumber(0)}</TableCell></TableRow>
                        <TableRow><TableCell sx={{ fontWeight: 700 }}>Total Area (ac)</TableCell><TableCell>{formatNumber(0, 5)}</TableCell></TableRow>
                      </TableBody>
                    </Table>
                  </Box>
                ))}
              </Stack>
            ) : (
              <TableContainer>
                <Table size="medium">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>Boom Width (ft)</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>Total Distance (ft)</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>Total Distance (mi)</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>Total Area (ac)</TableCell>
                    </TableRow>
                    {[1, 2, 3].map((idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ fontSize: '1rem' }}>Boom {idx}</TableCell>
                        <TableCell><Box sx={{ bgcolor: '#e53935', color: '#fff', px: 2, py: 0.5, borderRadius: 1, display: 'inline-block', fontSize: '0.9rem', fontWeight: 600, minWidth: 90, textAlign: 'center' }}>Unknown</Box></TableCell>
                        <TableCell sx={{ fontSize: '1rem' }}>{formatNumber(0)}</TableCell>
                        <TableCell sx={{ fontSize: '1rem' }}>{formatNumber(0)}</TableCell>
                        <TableCell sx={{ fontSize: '1rem' }}>{formatNumber(0)}</TableCell>
                        <TableCell sx={{ fontSize: '1rem' }}>{formatNumber(0)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell sx={{ fontSize: '1rem', fontWeight: 700 }}>Total</TableCell>
                      <TableCell><Box sx={{ bgcolor: '#e53935', color: '#fff', px: 2, py: 0.5, borderRadius: 1, display: 'inline-block', fontSize: '0.9rem', fontWeight: 600, minWidth: 90, textAlign: 'center' }}>Unknown</Box></TableCell>
                      <TableCell>-</TableCell><TableCell>-</TableCell><TableCell>-</TableCell><TableCell>-</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}
        {/* Flow Meter Results Grid */}
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" gutterBottom fontWeight={600}>
            Flow Meter Results
          </Typography>
          {diagnostic ? (
            isMobile ? (
              <Stack spacing={2}>
                {[
                  { label: 'Meter 1', pulses: diagnostic.totalPulses1, gallons: diagnostic.totalGallons1, appRate: diagnostic.appRate1 },
                  { label: 'Meter 2', pulses: diagnostic.totalPulses2, gallons: diagnostic.totalGallons2, appRate: diagnostic.appRate2 },
                  { label: 'Meter 3', pulses: diagnostic.totalPulses3, gallons: diagnostic.totalGallons3, appRate: diagnostic.appRate3 },
                  { label: 'Total', pulses: diagnostic.totalPulses, gallons: diagnostic.totalGallons, appRate: diagnostic.totalAppRate, bold: true },
                ].map((meter, idx) => (
                  <Box key={idx} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 1.5 }}>
                    <Table size="small">
                      <TableBody>
                        <TableRow><TableCell sx={{ fontWeight: 700 }}>Flow Meter</TableCell><TableCell sx={{ fontWeight: meter.bold ? 700 : 400 }}>{meter.label}</TableCell></TableRow>
                        <TableRow><TableCell sx={{ fontWeight: 700 }}>Total Pulses</TableCell><TableCell sx={{ fontWeight: meter.bold ? 700 : 400 }}>{meter.pulses}</TableCell></TableRow>
                        <TableRow><TableCell sx={{ fontWeight: 700 }}>Total Gallons</TableCell><TableCell sx={{ fontWeight: meter.bold ? 700 : 400 }}>{formatNumber(meter.gallons, 4)}</TableCell></TableRow>
                        <TableRow><TableCell sx={{ fontWeight: 700 }}>App Rate (gal/ac)</TableCell><TableCell sx={{ fontWeight: meter.bold ? 700 : 400 }}>{formatNumber(meter.appRate)}</TableCell></TableRow>
                      </TableBody>
                    </Table>
                  </Box>
                ))}
              </Stack>
            ) : (
              <TableContainer>
                <Table size="medium">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>Flow Meter</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>Total Pulses</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>Total Gallons</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>App Rate (gal/ac)</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontSize: '1rem' }}>Meter 1</TableCell>
                      <TableCell sx={{ fontSize: '1rem' }}>{diagnostic.totalPulses1}</TableCell>
                      <TableCell sx={{ fontSize: '1rem' }}>{formatNumber(diagnostic.totalGallons1, 4)}</TableCell>
                      <TableCell sx={{ fontSize: '1rem' }}>{formatNumber(diagnostic.appRate1)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontSize: '1rem' }}>Meter 2</TableCell>
                      <TableCell sx={{ fontSize: '1rem' }}>{diagnostic.totalPulses2}</TableCell>
                      <TableCell sx={{ fontSize: '1rem' }}>{formatNumber(diagnostic.totalGallons2, 4)}</TableCell>
                      <TableCell sx={{ fontSize: '1rem' }}>{formatNumber(diagnostic.appRate2)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontSize: '1rem' }}>Meter 3</TableCell>
                      <TableCell sx={{ fontSize: '1rem' }}>{diagnostic.totalPulses3}</TableCell>
                      <TableCell sx={{ fontSize: '1rem' }}>{formatNumber(diagnostic.totalGallons3, 4)}</TableCell>
                      <TableCell sx={{ fontSize: '1rem' }}>{formatNumber(diagnostic.appRate3)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>Total</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>{diagnostic.totalPulses}</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>{formatNumber(diagnostic.totalGallons, 4)}</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>{formatNumber(diagnostic.totalAppRate)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )
          ) : (
            <Typography>No data available.</Typography>
          )}
        </Paper>
      </Box>
    </div>
  );
};

export default AdminDeviceDiagnostic;